import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Plus, Edit2, Trash2, UserCheck, UserX, MoreVertical, Search, Filter } from 'lucide-react';
import { PageHeader } from '../../components/layout';
import {
    Card, CardBody, Table, Pagination, Button, Badge, Avatar,
    SearchInput, Dropdown, DropdownItem, DropdownDivider,
    Modal, ConfirmDialog, toast, EmptyState
} from '../../components/common';
import { userApi } from '../../services/api';
import UserForm from './UserForm';
import './Users.css';

export default function UserList() {
    const queryClient = useQueryClient();
    const [page, setPage] = useState(1);
    const [pageSize, setPageSize] = useState(10);
    const [search, setSearch] = useState('');
    const [showForm, setShowForm] = useState(false);
    const [editingUser, setEditingUser] = useState(null);
    const [deleteUser, setDeleteUser] = useState(null);

    // Fetch users
    const { data, isLoading, error } = useQuery({
        queryKey: ['users', page, pageSize, search],
        queryFn: () => userApi.list({ page, limit: pageSize, search }),
        select: (res) => res.data
    });

    // Delete mutation
    const deleteMutation = useMutation({
        mutationFn: (id) => userApi.delete(id),
        onSuccess: () => {
            toast.success('User deleted successfully');
            queryClient.invalidateQueries(['users']);
            setDeleteUser(null);
        },
        onError: (err) => {
            toast.error(err.response?.data?.error?.message || 'Failed to delete user');
        }
    });

    // Activate/Deactivate mutation
    const toggleStatusMutation = useMutation({
        mutationFn: ({ id, action }) =>
            action === 'activate' ? userApi.activate(id) : userApi.deactivate(id),
        onSuccess: (_, { action }) => {
            toast.success(`User ${action}d successfully`);
            queryClient.invalidateQueries(['users']);
        },
        onError: (err) => {
            toast.error(err.response?.data?.error?.message || 'Failed to update user status');
        }
    });

    const handleEdit = (user) => {
        setEditingUser(user);
        setShowForm(true);
    };

    const handleCloseForm = () => {
        setShowForm(false);
        setEditingUser(null);
    };

    const columns = [
        {
            key: 'user',
            title: 'User',
            dataIndex: 'fullName',
            render: (_, row) => (
                <div className="user-cell">
                    <Avatar name={row.fullName} size="sm" />
                    <div className="user-info">
                        <span className="user-name">{row.fullName}</span>
                        <span className="user-email">{row.email}</span>
                    </div>
                </div>
            )
        },
        {
            key: 'role',
            title: 'Role',
            dataIndex: 'role',
            render: (role) => (
                <Badge variant={getRoleVariant(role?.name)}>
                    {role?.name || 'No Role'}
                </Badge>
            )
        },
        {
            key: 'department',
            title: 'Department',
            dataIndex: 'department',
            render: (dept) => dept?.name || '-'
        },
        {
            key: 'status',
            title: 'Status',
            dataIndex: 'isActive',
            render: (isActive) => (
                <Badge variant={isActive ? 'success' : 'danger'} dot>
                    {isActive ? 'Active' : 'Inactive'}
                </Badge>
            )
        },
        {
            key: 'lastLogin',
            title: 'Last Login',
            dataIndex: 'lastLoginAt',
            render: (date) => date ? new Date(date).toLocaleDateString() : 'Never'
        },
        {
            key: 'actions',
            title: '',
            width: 50,
            align: 'center',
            render: (_, row) => (
                <Dropdown
                    trigger={
                        <button className="action-btn">
                            <MoreVertical size={16} />
                        </button>
                    }
                    align="right"
                >
                    {({ close }) => (
                        <>
                            <DropdownItem icon={Edit2} onClick={() => { close(); handleEdit(row); }}>
                                Edit User
                            </DropdownItem>
                            {row.isActive ? (
                                <DropdownItem
                                    icon={UserX}
                                    onClick={() => {
                                        close();
                                        toggleStatusMutation.mutate({ id: row.id, action: 'deactivate' });
                                    }}
                                >
                                    Deactivate
                                </DropdownItem>
                            ) : (
                                <DropdownItem
                                    icon={UserCheck}
                                    onClick={() => {
                                        close();
                                        toggleStatusMutation.mutate({ id: row.id, action: 'activate' });
                                    }}
                                >
                                    Activate
                                </DropdownItem>
                            )}
                            <DropdownDivider />
                            <DropdownItem
                                icon={Trash2}
                                danger
                                onClick={() => { close(); setDeleteUser(row); }}
                            >
                                Delete User
                            </DropdownItem>
                        </>
                    )}
                </Dropdown>
            )
        }
    ];

    const getRoleVariant = (roleName) => {
        const variants = {
            'Admin': 'danger',
            'Manager': 'warning',
            'Procurement': 'primary',
            'Finance': 'info',
            'Requester': 'default'
        };
        return variants[roleName] || 'default';
    };

    return (
        <div className="users-page">
            <PageHeader
                title="User Management"
                subtitle="Manage system users and their access"
                breadcrumbs={[
                    { label: 'Users', path: '/users' }
                ]}
                actions={
                    <Button icon={Plus} onClick={() => setShowForm(true)}>
                        Add User
                    </Button>
                }
            />

            <Card>
                <div className="users-toolbar">
                    <SearchInput
                        value={search}
                        onChange={setSearch}
                        onSearch={setSearch}
                        placeholder="Search users..."
                        className="users-search"
                    />
                </div>

                <Table
                    columns={columns}
                    data={data?.data || []}
                    loading={isLoading}
                    emptyText="No users found"
                    rowKey="id"
                />

                {data?.meta && (
                    <div className="users-pagination">
                        <Pagination
                            page={page}
                            totalPages={data.meta.totalPages}
                            pageSize={pageSize}
                            total={data.meta.total}
                            onPageChange={setPage}
                            onPageSizeChange={(size) => { setPageSize(size); setPage(1); }}
                        />
                    </div>
                )}
            </Card>

            {/* User Form Modal */}
            <Modal
                isOpen={showForm}
                onClose={handleCloseForm}
                title={editingUser ? 'Edit User' : 'Add New User'}
                size="lg"
            >
                <UserForm
                    user={editingUser}
                    onSuccess={() => {
                        handleCloseForm();
                        queryClient.invalidateQueries(['users']);
                    }}
                    onCancel={handleCloseForm}
                />
            </Modal>

            {/* Delete Confirmation */}
            <ConfirmDialog
                isOpen={!!deleteUser}
                onClose={() => setDeleteUser(null)}
                onConfirm={() => deleteMutation.mutate(deleteUser?.id)}
                title="Delete User"
                message={`Are you sure you want to delete "${deleteUser?.fullName}"? This action cannot be undone.`}
                confirmText="Delete"
                variant="danger"
                loading={deleteMutation.isPending}
            />
        </div>
    );
}
